import React, { useEffect, useRef } from 'react';

export default function HtmlEditorPage() {
  const editorRef  = useRef(null);
  const imgInputRef = useRef(null);

  useEffect(() => {
    const key = new URLSearchParams(window.location.search).get('key');
    if (key && editorRef.current) {
      const stored = localStorage.getItem(`html_editor_input_${key}`);
      editorRef.current.innerHTML = stored || '';
    }
    document.title = 'HTML Editor';
  }, []);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };

  // Compress image to JPEG ≤800px before inserting — keeps base64 small
  const insertImage = (file) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const MAX = 800;
      let w = img.naturalWidth;
      let h = img.naturalHeight;
      if (w > MAX || h > MAX) {
        if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
        else       { w = Math.round(w * MAX / h); h = MAX; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
      URL.revokeObjectURL(objectUrl);
      exec('insertImage', dataUrl);
    };
    img.src = objectUrl;
  };

  const insertLink = () => {
    const url = prompt('Enter URL:', 'https://');
    if (url) exec('createLink', url);
  };

  const handleDone = () => {
    const key  = new URLSearchParams(window.location.search).get('key');
    const html = editorRef.current?.innerHTML || '';
    if (key) {
      // Write to localStorage as backup (may fail if content is huge — that's OK)
      try { localStorage.setItem(`html_editor_result_${key}`, html); } catch (_) {}
      // Send content directly in postMessage — no size limit, always works
      window.opener?.postMessage({ type: 'html_editor_done', key, html }, '*');
      // Small delay so message reaches opener before the tab closes
      setTimeout(() => window.close(), 120);
    } else {
      window.close();
    }
  };

  const sep = {
    display: 'inline-block', width: 1,
    background: '#475569', height: 20, margin: '0 3px',
  };

  const Btn = ({ onClick, title, children }) => (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        background: 'transparent', border: '1px solid #475569', color: '#cbd5e1',
        padding: '4px 9px', borderRadius: 3, cursor: 'pointer', fontSize: 13,
        lineHeight: 1.4, whiteSpace: 'nowrap',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = '#334155'; e.currentTarget.style.color = '#f1f5f9'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#cbd5e1'; }}
    >
      {children}
    </button>
  );

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      {/* ── Toolbar ── */}
      <div style={{ background: '#1e293b', padding: '8px 14px', display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap', borderBottom: '2px solid #0f172a', flexShrink: 0 }}>

        <span style={{ color: '#64748b', fontSize: 11, fontWeight: 700, letterSpacing: 1, marginRight: 6 }}>HTML EDITOR</span>
        <div style={sep} />

        {/* Text style */}
        <Btn onClick={() => exec('bold')}          title="Bold (Ctrl+B)"><b>B</b></Btn>
        <Btn onClick={() => exec('italic')}        title="Italic (Ctrl+I)"><em>I</em></Btn>
        <Btn onClick={() => exec('underline')}     title="Underline (Ctrl+U)"><u>U</u></Btn>
        <Btn onClick={() => exec('strikeThrough')} title="Strikethrough"><s>S</s></Btn>
        <div style={sep} />

        {/* Headings */}
        <Btn onClick={() => exec('formatBlock', 'h1')}  title="Heading 1">H1</Btn>
        <Btn onClick={() => exec('formatBlock', 'h2')}  title="Heading 2">H2</Btn>
        <Btn onClick={() => exec('formatBlock', 'h3')}  title="Heading 3">H3</Btn>
        <Btn onClick={() => exec('formatBlock', 'p')}   title="Normal paragraph">¶</Btn>
        <Btn onClick={() => exec('formatBlock', 'pre')} title="Code block">&lt;/&gt;</Btn>
        <Btn onClick={() => exec('formatBlock', 'blockquote')} title="Blockquote">" "</Btn>
        <div style={sep} />

        {/* Lists */}
        <Btn onClick={() => exec('insertUnorderedList')} title="Bullet list">• List</Btn>
        <Btn onClick={() => exec('insertOrderedList')}   title="Numbered list">1. List</Btn>
        <div style={sep} />

        {/* Insert */}
        <Btn onClick={insertLink}                     title="Insert hyperlink">🔗 Link</Btn>
        <Btn onClick={() => imgInputRef.current?.click()} title="Insert image (auto-compressed to ≤800px JPEG)">🖼 Image</Btn>
        <input
          ref={imgInputRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={e => { if (e.target.files[0]) insertImage(e.target.files[0]); e.target.value = ''; }}
        />
        <Btn onClick={() => exec('insertHorizontalRule')} title="Insert horizontal rule">─ Rule</Btn>
        <div style={sep} />

        {/* Alignment */}
        <Btn onClick={() => exec('justifyLeft')}   title="Align left">⬱ Left</Btn>
        <Btn onClick={() => exec('justifyCenter')} title="Align center">≡ Center</Btn>
        <Btn onClick={() => exec('justifyRight')}  title="Align right">⬰ Right</Btn>
        <div style={sep} />

        {/* Indent */}
        <Btn onClick={() => exec('indent')}  title="Increase indent">→ Indent</Btn>
        <Btn onClick={() => exec('outdent')} title="Decrease indent">← Outdent</Btn>
        <div style={sep} />

        {/* Font colour */}
        <label title="Text colour" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: '#cbd5e1', fontSize: 13, border: '1px solid #475569', padding: '4px 8px', borderRadius: 3 }}>🎨 Color</span>
          <input type="color" defaultValue="#000000" style={{ display: 'none' }}
            onChange={e => exec('foreColor', e.target.value)} />
        </label>
        <div style={sep} />

        {/* History */}
        <Btn onClick={() => exec('undo')}          title="Undo (Ctrl+Z)">↩ Undo</Btn>
        <Btn onClick={() => exec('redo')}          title="Redo (Ctrl+Y)">↪ Redo</Btn>
        <Btn onClick={() => exec('removeFormat')}  title="Clear all formatting from selection">✕ Clear</Btn>

        <div style={{ flex: 1 }} />

        <button
          type="button"
          title="Save content and close editor"
          onClick={handleDone}
          style={{ background: '#22c55e', color: 'white', border: 'none', padding: '7px 22px', borderRadius: 4, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}
        >
          ✓ Done
        </button>
      </div>

      {/* ── Status bar ── */}
      <div style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '3px 64px', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>Click in the area below to start editing · Hover over toolbar buttons to see tooltips</span>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>Images are compressed to ≤800 px JPEG automatically</span>
      </div>

      {/* ── Editor canvas ── */}
      <div
        ref={editorRef}
        contentEditable
        style={{
          flex: 1, overflowY: 'auto', outline: 'none',
          padding: '36px 64px',
          fontSize: 15, lineHeight: 1.8, color: '#1e293b',
          caretColor: '#3b82f6',
          maxWidth: 960, margin: '0 auto', width: '100%', boxSizing: 'border-box',
        }}
      />
    </div>
  );
}
